import React from "react";
import { motion } from "framer-motion";
import { Award, TrendingUp, TrendingDown } from "lucide-react";
import { getGradeGradientClass, getGradeTextClass } from "../gradeColors";

function getPerformanceLabel(score) {
	if (score >= 90) return "Excellent Performance";
	if (score >= 70) return "Good Foundation";
	if (score >= 50) return "Needs Improvement";
	return "Critical Issues";
}

function getConfidenceFillClass(confidenceScore) {
	if (confidenceScore >= 0.7) return "grade-hero__confidence-fill--high";
	if (confidenceScore >= 0.4) return "grade-hero__confidence-fill--medium";
	return "grade-hero__confidence-fill--low";
}

export default function GradeHero({
	score,
	grade,
	gradeLabel,
	confidenceScore,
	confidenceLabel,
}) {
	const gradientClass = getGradeGradientClass(grade);
	const textColorClass = getGradeTextClass(grade);

	return (
		<motion.div
			className="grade-hero"
			initial={{ opacity: 0, scale: 0.95 }}
			animate={{ opacity: 1, scale: 1 }}
			transition={{ duration: 0.6, delay: 0.2 }}
		>
			<div className="grade-hero__bg-grid bg-grid-pattern" />

			<div className="grade-hero__inner">
				<div className="grade-hero__layout">
					<div className="grade-hero__left">
						<div className="grade-hero__label-row">
							<Award className="grade-hero__label-icon" />
							<span className="grade-hero__label">Overall AEO Grade</span>
						</div>

						<div className="grade-hero__scores">
							<motion.div
								className={`grade-hero__grade ${gradientClass}`}
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
							>
								{grade}
							</motion.div>

							<div className="grade-hero__score-block">
								<motion.div
									className={`grade-hero__score-value ${textColorClass}`}
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ delay: 0.6 }}
								>
									{score.toFixed(1)}
								</motion.div>
								<div className="grade-hero__score-max">out of 100</div>
							</div>
						</div>

						<motion.p
							className="grade-hero__grade-label"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.7 }}
						>
							{gradeLabel}
						</motion.p>
					</div>

					<div className="grade-hero__right">
						{confidenceScore !== undefined && (
							<motion.div
								className="grade-hero__side-card"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.8 }}
							>
								<div className="grade-hero__side-card-title">Confidence Level</div>
								<div className="grade-hero__confidence-row">
									<div className="grade-hero__confidence-bar">
										<motion.div
											className={`grade-hero__confidence-fill ${getConfidenceFillClass(confidenceScore)}`}
											initial={{ width: 0 }}
											animate={{ width: `${confidenceScore * 100}%` }}
											transition={{ duration: 1, delay: 0.9 }}
										/>
									</div>
									<div className="grade-hero__confidence-pct">
										{(confidenceScore * 100).toFixed(0)}%
									</div>
								</div>
								<div className="grade-hero__confidence-label">
									{confidenceLabel || "Medium"} confidence
								</div>
							</motion.div>
						)}

						<motion.div
							className="grade-hero__side-card"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 1 }}
						>
							<div className="grade-hero__indicator-row">
								{score >= 70 ? (
									<TrendingUp className="grade-hero__trend-icon grade-hero__trend-icon--up" />
								) : (
									<TrendingDown className="grade-hero__trend-icon grade-hero__trend-icon--down" />
								)}
								<div>
									<div className="grade-hero__indicator-title">
										{getPerformanceLabel(score)}
									</div>
									<div className="grade-hero__indicator-sub">
										Based on 13 AEO categories
									</div>
								</div>
							</div>
						</motion.div>
					</div>
				</div>
			</div>
		</motion.div>
	);
}
